import { isFunction, object } from 'underscore';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { localize, FilterBarIngoreClickOutsideClassName } from '@bingads-webui/filter-commons';
import { flattenEntitySchema, buildNameToPropertyMapping } from '@bingads-webui/filter-schema';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { FilterBar } from '@bingads-webui/filter-bar-react';
import { PredicateListReadonly } from '@bingads-webui/predicate-list-readonly-react';
import { setTrimmedFilterDataOptionsDefaultSearchInsightsReport } from '../../reports-v4-report-preview/src/helpers/dynamic-filter-options-helper';

const getStyles = () => ({
  predicateList: {
    maxWidth: '398px',
    '& .predicates': {
      minWidth: 'initial',
    },
    '& .add-filter-button': {
      margin: 0,
    },
    '& .iconba': {
      fontSize: '14px !important',
    },
  },
});

class FilterViewManagerUnthemed extends React.PureComponent {
  constructor(props) {
    super(props);

    this.initializeEntitySchema(props);

    this.maximumPredicateLimit = Number.MAX_SAFE_INTEGER;

    this.state = {
      showFilterBar: false,
      selectableFilter: null,
      openedPredicateIndex: null,
      savedFilterNames: [],
    };

    if (props.searchbox) {
      this.setupSearchboxFilterOnToolbar();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.entitySchema !== this.props.entitySchema) {
      this.initializeEntitySchema(this.props);
      this.forceUpdate();
    }
  }

  onEditableFilterUpdate = (query, source = 'regular-filter') => {
    this.props.setEditableFilter(query, source);
    this.setState({
      openedPredicateIndex: null,
    });
  };

  onReset = () => {
    this.props.setEditableFilter({}, 'reset-filter');
    this.setState({
      selectableFilter: null,
      openedPredicateIndex: null,
    });
    this.hideFilterBar();
    if (isFunction(this.props.updateWaterfallFilter)) {
      this.props.updateWaterfallFilter(null);
    }
  };

  setupSearchboxFilterOnToolbar = () => {
    const id = 'search-box-filter';
    const searchBoxItem = {
      id,
      type: 'search-box',
      placeholder: this.props.searchbox.placeholder,
      handleChange: (value) => {
        if (value) {
          const query = object(
            [this.props.searchbox.searchProperty],
            [object([this.props.searchbox.searchOperator || '$contains'], [value])]
          );
          this.onEditableFilterUpdate(query, id);
        }
      },
    };
    this.props.updateToolbar(searchBoxItem);
  };

  setupPredicateListOnToolbar = (filterEntity) => {
    const predicateListView = (
      <PredicateListReadonly
        selector={this.props.selector}
        entitySchema={this.entitySchema}
        i18n={this.props.i18n}
        selectableFilter={this.state.selectableFilter}
        editableFilter={filterEntity}
        maximumPredicateLimit={this.maximumPredicateLimit}
        onClickPredicate={(openedPredicateIndex) => {
          this.setState({
            openedPredicateIndex,
            showFilterBar: true,
          });
          this.props.resetToolbarVisibility({
            forceHideToolbar: true,
          });
        }}
        onSelectableFilterUpdate={this.onSelectableFilterUpdate}
        hideAddFilterButton={this.props.hideAddFilterButton}
        getPredicateIconClassName={this.props.getPredicateIconClassName}
      />);
    const newItem = {
      id: 'predicate-list',
      type: 'custom',
      content: predicateListView,
      classes: this.props.classes.predicateList,
    };
    this.props.updateToolbar(newItem);
  };

  initializeEntitySchema = (props) => {
    this.entitySchema = localize(
      flattenEntitySchema(props.entitySchema),
      props.schemaI18n,
      props.legacyI18n
    );

    const newEntitySchema = setTrimmedFilterDataOptionsDefaultSearchInsightsReport(props.editableFilter, this.entitySchema);

    this.odataOptions = {
      fieldMap: buildNameToPropertyMapping(newEntitySchema),
    };
  };

  createFilterEntity(query, name, id) {
    return new FilterEntity(
      query,
      {
        entitySchema: this.entitySchema,
        odata: this.odataOptions,
      },
      name,
      id
    );
  }

  hideFilterBar = () => {
    this.setState({
      showFilterBar: false,
    });
    this.props.resetToolbarVisibility({
      forceHideToolbar: false,
    });
  };

  render() {
    const filterEntity = this.createFilterEntity(this.props.editableFilter);
    if (this.state.showFilterBar) {
      return (
        <Fragment>
          <FilterBar
            key={this.entitySchema.name}
            entitySchema={this.entitySchema}
            i18n={this.props.i18n}
            legacyI18n={this.props.legacyI18n}
            selectableFilter={this.state.selectableFilter}
            editableFilter={filterEntity}
            openedPredicateIndex={this.state.openedPredicateIndex}
            readOnlyProperties={this.props.readOnlyProperties}
            disallowDuplicateFilters={this.props.disallowDuplicateFilters}
            onEditableFilterUpdate={this.onEditableFilterUpdate}
            onSelectableFilterUpdate={this.onSelectableFilterUpdate}
            selector={this.props.selector}
            hideFilterBar={this.hideFilterBar}
            onSaveFilter={this.onSaveFilter}
            savedFilters={this.state.savedFilterNames}
            onReset={this.onReset}
            onDeleteFilters={this.onDeleteFilters}
            outsideClickIgnoreClass={FilterBarIngoreClickOutsideClassName}
            updateWaterfallFilter={this.props.updateWaterfallFilter}
            onApplySavedFilter={this.onApplySavedFilter}
            isPoPEnabled={this.props.isPoPEnabled}
            hideSaveFilterButton={this.props.hideSaveFilterButton}
            disabledSaveFilterButtonWithReason={this.props.disabledSaveFilterButtonWithReason}
            maximumPredicateLimit={this.maximumPredicateLimit}
            hideAddFilterButton={this.props.hideAddFilterButton}
            getPredicateIconClassName={this.props.getPredicateIconClassName}
          />
        </Fragment>
      );
    }
    this.setupPredicateListOnToolbar(filterEntity);
    return null;
  }
}

FilterViewManagerUnthemed.propTypes = {
  schemaI18n: PropTypes.shape({
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }),
  }).isRequired,
  editableFilter: PropTypes.shape({
  }).isRequired,
  entitySchema: PropTypes.shape({
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  searchbox: PropTypes.shape({
    searchProperty: PropTypes.string,
    searchOperator: PropTypes.string,
    placeholder: PropTypes.string,
  }),
  selector: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.arrayOf(PropTypes.string),
    })),
    removable: PropTypes.bool,
    defaultValue: PropTypes.arrayOf(PropTypes.string),
  })),
  updateWaterfallFilter: PropTypes.func,
  classes: PropTypes.shape({
    predicateList: PropTypes.string.isRequired,
  }).isRequired,
  isPoPEnabled: PropTypes.bool,
  readOnlyProperties: PropTypes.arrayOf(PropTypes.string),
  disallowDuplicateFilters: PropTypes.bool,
  hideSaveFilterButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  disabledSaveFilterButtonWithReason: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hideAddFilterButton: PropTypes.bool,
  instrumentation: PropTypes.shape({
    writeRawLog: PropTypes.func,
  }),
  setEditableFilter: PropTypes.func.isRequired,
  getPredicateIconClassName: PropTypes.func,
};

FilterViewManagerUnthemed.defaultProps = {
  selector: [],
  searchbox: undefined,
  updateWaterfallFilter: null,
  isPoPEnabled: false,
  readOnlyProperties: [],
  hideSaveFilterButton: false,
  disabledSaveFilterButtonWithReason: '',
  hideAddFilterButton: false,
  instrumentation: null,
  disallowDuplicateFilters: false,
  getPredicateIconClassName: undefined,
};

export const FilterViewManager =
  withInstrumentation(withDefaultStyles(FilterViewManagerUnthemed, getStyles));
